import { Button, Select, SettingsCard, Typography, EnterpriseBadge, useToast } from "@humansignal/ui";
import { IconUpload } from "@humansignal/icons";
import { useCallback, useContext, useEffect, useState } from "react";
import { ProjectLogoPicker } from "./ProjectLogoPicker";
import { PlanCard } from "./PlanCard";
import { useUpdatePageTitle, createTitleFromSegments } from "@humansignal/core";
import { Form, Input, TextArea, Toggle } from "../../components/Form";
import { RadioGroup } from "../../components/Form/Elements/RadioGroup/RadioGroup";
import { ProjectContext } from "../../providers/ProjectProvider";
import { cn } from "../../utils/bem";
import { useAPI } from "../../providers/ApiProvider";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { SettingsPageHeader } from "../../components/SettingsPageHeader/SettingsPageHeader";
import { SaveTemplateModal } from "./SaveTemplateModal";
import { useTranslation } from "../../i18n/LocaleProvider";
import { SUPPORTED_LANGUAGES } from "@humansignal/core";

/**
 * Per-project logo. Same upload mechanism as the org avatar / user
 * avatar: a hidden file input POSTs multipart to ``projectLogo`` (server
 * validates via ``check_avatar``), DELETE clears it. Falls back to the
 * coloured initials badge — the same fallback the project cards use — when
 * no logo is set. Its own action, not part of the settings form submit.
 */
const ProjectLogoUpload = ({ project, onChanged }) => {
  const api = useAPI();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [logo, setLogo] = useState(project?.logo || "");
  const [pickerOpen, setPickerOpen] = useState(false);
  useEffect(() => {
    setLogo(project?.logo || "");
  }, [project?.logo]);

  const initials = (project?.title || "?").trim()[0]?.toUpperCase() ?? "?";
  const color = project?.color && project.color !== "#FFFFFF" ? project.color : "var(--color-primary-surface)";

  // One upload path for both the gallery pick and a custom file (the picker
  // hands both up as a File) — same as the profile avatar infrastructure.
  const uploadFile = async (file) => {
    setBusy(true);
    const form = new FormData();
    form.append("logo", file);
    // ``Content-Type: multipart/form-data`` makes the API proxy drop the
    // header so fetch sets the correct multipart boundary. Passing ``{}``
    // leaves the default ``application/json`` in place, which the upload
    // endpoint rejects (415 unsupported media type).
    const res = await api.callApi("projectLogo", {
      params: { pk: project.id },
      body: form,
      headers: { "Content-Type": "multipart/form-data" },
      suppressError: true,
    });
    setBusy(false);
    if (res && res.logo) {
      setLogo(res.logo);
      setPickerOpen(false);
      toast.show({ message: "Logo updated." });
      onChanged?.();
    } else {
      const detail = res?.response?.detail || res?.response?.[0] || res?.error;
      toast.show({ message: typeof detail === "string" ? detail : "Couldn't upload the logo.", type: "error" });
    }
  };

  const remove = async () => {
    setBusy(true);
    const res = await api.callApi("deleteProjectLogo", { params: { pk: project.id }, suppressError: true });
    setBusy(false);
    if (res === undefined || !res?.error) {
      setLogo("");
      toast.show({ message: "Logo removed." });
      onChanged?.();
    } else {
      toast.show({ message: res?.error || "Couldn't remove the logo.", type: "error" });
    }
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
      <button
        type="button"
        onClick={() => setPickerOpen(true)}
        disabled={busy}
        aria-label="Choose project logo"
        style={{
          width: 56,
          height: 56,
          borderRadius: 12,
          flexShrink: 0,
          padding: 0,
          border: "1px solid var(--color-neutral-border)",
          overflow: "hidden",
          cursor: "pointer",
          background: logo ? "var(--color-neutral-background)" : color,
          display: "grid",
          placeItems: "center",
        }}
      >
        {logo ? (
          <img src={logo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 20 }}>{initials}</span>
        )}
      </button>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            type="button"
            look="outlined"
            size="small"
            leading={<IconUpload style={{ width: 16, height: 16 }} />}
            onClick={() => setPickerOpen(true)}
            disabled={busy}
          >
            {busy ? "Saving…" : logo ? "Change logo" : "Choose logo"}
          </Button>
          {logo && (
            <Button type="button" look="outlined" size="small" variant="negative" onClick={remove} disabled={busy}>
              Remove
            </Button>
          )}
        </div>
        <span style={{ fontSize: "var(--font-size-body-small)", color: "var(--color-neutral-content-subtler)" }}>
          Pick a logo from the gallery or upload your own.
        </span>
      </div>
      <ProjectLogoPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={uploadFile} busy={busy} />
    </div>
  );
};

export const GeneralSettings = () => {
  const { t } = useTranslation();
  const { project, fetchProject } = useContext(ProjectContext);
  const api = useAPI();
  const toast = useToast();
  const { user } = useAuth();
  const isStaff = !!(user?.is_staff || user?.is_superuser);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [savingWorkspace, setSavingWorkspace] = useState(false);

  useUpdatePageTitle(createTitleFromSegments([project?.title, "General Settings"]));

  useEffect(() => {
    api.callApi("workspaces", { suppressError: true }).then((res) => {
      if (res && !res.error) setWorkspaces(res.results ?? []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setWorkspace = useCallback(
    async (e) => {
      const raw = e.target.value;
      if (!project?.id) return;
      setSavingWorkspace(true);
      const res = await api.callApi("projectSetWorkspace", {
        params: { pk: project.id },
        body: { workspace_id: raw || null },
      });
      setSavingWorkspace(false);
      if (!res || res.error) {
        toast.show({ message: res?.error ?? "Failed to move project", type: "error" });
        return;
      }
      fetchProject(project.id, true);
    },
    [project?.id, api, toast, fetchProject],
  );

  const updateProject = useCallback(() => {
    if (project.id) fetchProject(project.id, true);
  }, [project]);

  const openTemplateModal = useCallback(() => setTemplateModalOpen(true), []);
  const closeTemplateModal = useCallback(() => setTemplateModalOpen(false), []);
  const onTemplateSaved = useCallback(
    (tpl) => {
      toast.show({ message: `Saved "${tpl.name}" to your templates` });
    },
    [toast],
  );

  const colors = ["#FDFDFC", "#FF4C25", "#FF750F", "#ECB800", "#9AC422", "#34988D", "#617ADA", "#CC6FBE"];

  // ``value`` maps to the backend choice as "<value> sampling"
  // (Project.SAMPLING_CHOICES); ``label`` is the full display label —
  // "Active learning" reads wrong with " sampling" appended, so labels
  // are spelled out per option instead of templated.
  const samplings = [
    { value: "Sequential", label: "Sequential sampling", description: "Tasks are ordered by Task ID" },
    { value: "Uniform", label: "Random sampling", description: "Tasks are chosen with uniform random" },
    {
      value: "Uncertainty",
      label: "Active learning",
      description: "Lowest model-confidence tasks first, requires an ML backend providing prediction scores.",
    },
  ];

  return (
    <div className={cn("general-settings").toClassName()}>
      <div className={cn("settings-page").toClassName()}>
        <SettingsPageHeader
          title={t("pages.settings.general_settings.general")}
          subtitle={t("pages.settings.general_settings.core_metadata_appearance_and_taskdistrib")}
        />
        <Form action="updateProject" formData={{ ...project }} params={{ pk: project.id }} onSubmit={updateProject}>
          <div className={cn("settings-page").elem("body").toClassName()}>
            <SettingsCard
              title={t("pages.settings.general_settings.project_details")}
              description={t("pages.settings.general_settings.core_information_shown_in_the_sidebar_an")}
            >
              <Form.Row columnCount={1}>
                <Input name="title" label={t("pages.settings.general_settings.project_name")} />
                <TextArea
                  name="description"
                  label={t("pages.settings.general_settings.description")}
                  style={{ minHeight: 128 }}
                />
                <div className="flex flex-col gap-2">
                  <label htmlFor="project_workspace" className="font-medium">
                    {t("pages.settings.general_settings.workspace")}
                  </label>
                  <select
                    id="project_workspace"
                    className="border bg-neutral-surface"
                    value={project?.workspace ?? ""}
                    onChange={setWorkspace}
                    disabled={savingWorkspace}
                    style={{
                      maxWidth: 420,
                      height: 40,
                      minHeight: 40,
                      boxSizing: "border-box",
                      padding: "0 16px",
                      borderRadius: 5,
                      fontSize: "var(--font-size-field-input)",
                      lineHeight: "var(--line-height-snug)",
                    }}
                  >
                    <option value="">{t("pages.settings.general_settings.unassigned")}</option>
                    {workspaces.map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.title}
                      </option>
                    ))}
                  </select>
                  <Typography size="small" className="text-neutral-content-subtler">
                    Group this project with others in your org. Manage workspaces from{" "}
                    <a href="/organization/workspaces" data-external className="underline">
                      {t("pages.settings.general_settings.organization_workspaces")}
                    </a>
                    .
                  </Typography>
                </div>
              </Form.Row>
            </SettingsCard>

            <SettingsCard
              title={t("pages.settings.general_settings.appearance")}
              description={t("pages.settings.general_settings.visual_identifier_for_this_project")}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
                <label className="font-medium" style={{ fontSize: "var(--font-size-body-small)" }}>
                  Logo
                </label>
                <ProjectLogoUpload project={project} onChanged={() => project?.id && fetchProject(project.id, true)} />
              </div>
              <RadioGroup name="color" label={null} size="large" labelProps={{ size: "large" }}>
                {colors.map((color) => (
                  <RadioGroup.Button key={color} value={color}>
                    <div className={cn("color").toClassName()} style={{ "--background": color }} />
                  </RadioGroup.Button>
                ))}
              </RadioGroup>
            </SettingsCard>

            <SettingsCard
              title={t("pages.settings.general_settings.task_sampling")}
              description={t("pages.settings.general_settings.how_tasks_are_distributed_to_annotators")}
            >
              <RadioGroup label={null} labelProps={{ size: "large" }} name="sampling" simple>
                {samplings.map(({ value, label, description }) => (
                  <RadioGroup.Button key={value} value={`${value} sampling`} label={label} description={description} />
                ))}
              </RadioGroup>
            </SettingsCard>

            <SettingsCard
              title={t("pages.settings.general_settings.annotator_language_title")}
              description={t("pages.settings.general_settings.annotator_language_help")}
            >
              <Form.Row columnCount={1}>
                <div className="flex flex-col gap-2">
                  <label htmlFor="annotator_language" className="font-medium">
                    {t("pages.settings.general_settings.annotator_language_label")}
                  </label>
                  <select
                    id="annotator_language"
                    name="annotator_language"
                    className="border bg-neutral-surface"
                    defaultValue={project?.annotator_language ?? ""}
                    style={{
                      maxWidth: 420,
                      height: 40,
                      minHeight: 40,
                      boxSizing: "border-box",
                      padding: "0 16px",
                      borderRadius: 5,
                      fontSize: "var(--font-size-field-input)",
                      lineHeight: "var(--line-height-snug)",
                    }}
                  >
                    <option value="">{t("pages.settings.general_settings.annotator_language_inherit")}</option>
                    {SUPPORTED_LANGUAGES.map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.endonym} ({l.name})
                      </option>
                    ))}
                  </select>
                  <Typography size="small" className="text-neutral-content-subtler">
                    {t("pages.settings.general_settings.annotator_language_caption")}
                  </Typography>
                </div>
              </Form.Row>
            </SettingsCard>

            <SettingsCard
              title={t("pages.settings.general_settings.local_mirror_title")}
              description={t("pages.settings.general_settings.local_mirror_help")}
            >
              <Form.Row columnCount={1}>
                <Toggle
                  name="allow_metadata_mirror"
                  label={t("pages.settings.general_settings.allow_metadata_mirror_label")}
                  description={t("pages.settings.general_settings.allow_metadata_mirror_description")}
                />
                <Toggle
                  name="allow_offline_for_annotators"
                  label={t("pages.settings.general_settings.allow_offline_annotators_label")}
                  description={t("pages.settings.general_settings.allow_offline_annotators_description")}
                />
                <Toggle
                  name="allow_offline_for_reviewers"
                  label={t("pages.settings.general_settings.allow_offline_reviewers_label")}
                  description={t("pages.settings.general_settings.allow_offline_reviewers_description")}
                />
                <Toggle
                  name="allow_on_mobile"
                  label={t("pages.settings.general_settings.allow_on_mobile_label", undefined, "Allow on mobile")}
                  description={t(
                    "pages.settings.general_settings.allow_on_mobile_description",
                    undefined,
                    "When on, annotators and reviewers can download this project to the AfriAnnotate mobile app for offline labeling. Enable only for projects whose annotation interface works on phone screens (choice selection, tap-to-classify, simple text). Defaults off.",
                  )}
                />
              </Form.Row>
            </SettingsCard>

            <SettingsCard
              title={t("pages.settings.general_settings.data_manager_access")}
              description={t("pages.settings.general_settings.independently_control_whether_annotators")}
            >
              <Form.Row columnCount={1}>
                <Toggle
                  name="annotators_see_data_manager"
                  label={t("pages.settings.general_settings.annotators_can_open_the_data_manager")}
                  description={t("pages.settings.general_settings.when_off_the_data_manager_tab_is_hidden_")}
                />
                <Toggle
                  name="reviewers_see_data_manager"
                  label={t("pages.settings.general_settings.reviewers_can_open_the_data_manager")}
                  description={t("pages.settings.general_settings.when_off_the_data_manager_tab_is_hidden__2")}
                />
              </Form.Row>
            </SettingsCard>
          </div>

          <Form.Actions>
            <Form.Indicator>
              <span case="success">{t("pages.settings.general_settings.saved")}</span>
            </Form.Indicator>
            <Button
              type="button"
              look="outlined"
              onClick={openTemplateModal}
              aria-label={t("pages.settings.general_settings.save_this_project_as_a_reusable_template")}
            >
              Save as template
            </Button>
            <Button
              type="submit"
              className="w-[120px]"
              aria-label={t("pages.settings.general_settings.save_general_settings")}
            >
              Save
            </Button>
          </Form.Actions>
        </Form>
        {/* The plan is one JSON field with its own save, outside the form. */}
        <div className={cn("settings-page").elem("body").toClassName()} style={{ marginTop: 16 }}>
          <PlanCard />
        </div>
      </div>
      <SaveTemplateModal
        open={templateModalOpen}
        project={project}
        isStaff={isStaff}
        onClose={closeTemplateModal}
        onSaved={onTemplateSaved}
      />
    </div>
  );
};

// ``menuItem`` drives the sidebar label; ``title`` is what the
// RoutesProvider uses to build the breadcrumb chain (it filters out
// routes without a ``title``). Setting both keeps sidebar text +
// breadcrumb text in sync without rendering "General" as two words
// next to each other in the sidebar.
GeneralSettings.menuItem = "General";
GeneralSettings.title = "General";
GeneralSettings.path = "/";
GeneralSettings.exact = true;
